import { MatCardModule } from '@angular/material/card';
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Resource } from '../../models/resource';
import { ResourceService } from '../../services/resource.service';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-resource-revel',
  templateUrl: './resource-revel.component.html',
  styleUrls: ['./resource-revel.component.scss'],
  standalone: false
})
export class ResourceRevelComponent implements OnInit {
  @Input() revelId!: number;
  @Input() revelDate!: string; // Optional: if provided, used for folder naming
  @Input() viewMode = true;
  resources: Resource[] = [];
  selectedResource: Resource | null = null;
  resourceForm!: FormGroup;
  showResourceForm = false;
  selectedFile: File | null = null;

  constructor(private resourceService: ResourceService, private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initForm();
    if (this.revelId) {
      this.loadResources();
    }
  }

  private initForm(): void {
    this.resourceForm = this.fb.group({
      name: ['', Validators.required],
      isHyperlink: [false],
      resourceType: ['', Validators.required],
      revelId: [null]
    });
  }

  loadResources(): void {
    this.resourceService.getResourcesByRevel(this.revelId).subscribe({
      next: (resources) => {
        this.resources = resources;
      },
      error: (error) => {
        console.error('Error loading resources:', error);
      }
    });
  }

  addResource(): void {
    this.selectedResource = null;
    this.resourceForm.reset({
      isHyperlink: false
    });
    this.showResourceForm = true;
  }

  editResource(resource: Resource): void {
    this.selectedResource = resource;
    this.resourceForm.patchValue(resource);
    this.showResourceForm = true;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      console.log('Selected file:', this.selectedFile);
    }
  }

  onIsHyperlinkChange(): void {
    if (this.resourceForm.get('isHyperlink')?.value) {
      this.selectedFile = null; // Clear the selected file if "Is Hyperlink" is true
      console.log('File selector disabled because "Is Hyperlink" is true.');
    }
  }

  saveResource(): void {
    if (this.resourceForm.valid) {
      const resourceData: Resource = {
        ...this.resourceForm.value,
        revelId: this.revelId,
        id: this.selectedResource?.id || undefined
      };

      console.log('Save Resource.');

      if (!this.resourceForm.get('isHyperlink')?.value) {
        const fileName = this.selectedFile?.name || '';
        if (this.revelDate && this.revelDate.trim().length > 0) {
          resourceData.path = `revel/${this.revelDate}/${fileName}`;
        } else if (this.revelId) {
          // Fallback to revel ID-based folder when date isn't available in the UI
          resourceData.path = `revel/${this.revelId}/${fileName}`;
        } else {
          console.error('Revel identifier missing.');
          return;
        }
      }
      resourceData.resourceEntity = 'Revel';

      console.log('path = ', resourceData.path);

      if (this.selectedResource) {
        console.log('Update Resource.');
        this.resourceService.updateResource(this.selectedResource.id, resourceData).subscribe({
          next: () => {
            this.loadResources();
            this.closeForm();
          },
          error: (error) => {
            console.error('Error updating resource:', error);
          }
        });
      } else {
        console.log('Create Resource.');
        this.resourceService.createResource(resourceData).subscribe({
          next: () => {
            if (this.selectedFile) {
              const formData = new FormData();
              formData.append('file', this.selectedFile);
              this.resourceService.uploadResource(formData, resourceData.path).subscribe({
                next: () => {
                  console.log('File uploaded successfully');
                },
                error: (error) => {
                  console.error('Error uploading file:', error);
                }
              });
            }
            this.loadResources();
            this.closeForm();
          },
          error: (error) => {
            console.error('Error creating resource:', error);
          }
        });
      }
    }
  }

  deleteResource(id: number): void {
    if (confirm('Are you sure you want to delete this resource?')) {
      this.resourceService.deleteResource(id).subscribe({
        next: () => {
          this.loadResources();
        },
        error: (error) => {
          console.error('Error deleting resource:', error);
        }
      });
    }
  }

  closeForm(): void {
    this.selectedResource = null;
    this.showResourceForm = false;
    this.resourceForm.reset();
  }

  downloadResource(resource: Resource): void {
    if (!resource.isHyperlink) {
      if (!resource.path) {
        console.error('Resource path is empty or invalid:', resource);
        return;
      }
      this.resourceService.downloadResourceResponse(resource.path).subscribe({
        next: (res) => {
          const blob = res.body as Blob;
          if (!blob || blob.size === 0) {
            console.error('Downloaded file is empty. Check the backend response.');
            return;
          }
          const cd = res.headers.get('Content-Disposition');
          const fallbackName = resource.path.split('/').pop() || resource.name || 'resource';
          const filename = this.extractFilename(cd) || fallbackName;
          const inferredType = this.inferMimeType(filename) || blob.type || 'application/octet-stream';
          const displayBlob = blob.type && blob.type !== '' ? blob : new Blob([blob], { type: inferredType });
          const viewable = inferredType.startsWith('image/') || inferredType === 'application/pdf' || inferredType.startsWith('text/') || inferredType === 'application/json' || inferredType.startsWith('audio/') || inferredType.startsWith('video/');
          const objectUrl = window.URL.createObjectURL(displayBlob);
          if (viewable) {
            const win = window.open(objectUrl, '_blank');
            if (!win) {
              console.warn('Popup blocked. Please allow popups for this site.');
            }
            setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
          } else {
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(objectUrl);
          }
        },
        error: (error) => {
          console.error('Error fetching resource for viewing:', error);
        }
      });
    }
  }

  onResourceClick(event: Event, resource: Resource): void {
    event.preventDefault(); // Prrevel default browser behavior
    event.stopPropagation(); // Stop revel propagation

    console.log('Resource clicked:', resource); // Log the resource for debugging

    if (!resource.isHyperlink) {
      this.downloadResource(resource); // Now opens in a new tab
    }
  }

  private inferMimeType(fileName: string): string {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'png': return 'image/png';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'gif': return 'image/gif';
      case 'webp': return 'image/webp';
      case 'svg': return 'image/svg+xml';
      case 'pdf': return 'application/pdf';
      case 'txt': return 'text/plain';
      case 'html':
      case 'htm': return 'text/html';
      case 'json': return 'application/json';
      case 'mp3': return 'audio/mpeg';
      case 'wav': return 'audio/wav';
      case 'mp4': return 'video/mp4';
      case 'mov': return 'video/quicktime';
      default: return 'application/octet-stream';
    }
  }

  private extractFilename(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;
    const filenameStarMatch = /filename\*=UTF-8''([^;\n]+)/i.exec(contentDisposition);
    if (filenameStarMatch && filenameStarMatch[1]) {
      try { return decodeURIComponent(filenameStarMatch[1]); } catch { /* ignore */ }
    }
    const filenameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return filenameMatch && filenameMatch[1] ? filenameMatch[1] : null;
  }
}
